import { useState } from "react"
import { Bot, Check, UserMinus, UserRound, UserRoundPlus } from "lucide-react"
import { useApp } from "../../state/AppState"
import { Avatar, Button, Input, Popover, cx } from "../ui"
import { relativeTime } from "../../lib/format"

/**
 * Who has to answer next.
 *
 * Only the author of the newest entry can change this - they have just spoken,
 * so they are the one who knows who the ball is now with. Everyone else sees
 * the state and why they cannot change it.
 *
 * The project's agent (Claude) is offered alongside the members even when it
 * is not on the issue yet - assigning it joins it. Assigning a resolved issue
 * reopens it, so a question Claude closed can be handed straight back.
 */
export function AssignBar({ issue, members, agents = [], onAssign, onInvite, busy }) {
	const { user, timezone } = useApp()
	const [note, setNote] = useState("")

	const closed = issue.status === "closed"
	const candidates = [
		...members,
		...agents.filter((agent) => !members.some((m) => m.user_id === agent.user_id))
	]
	const assignee = issue.assignee_id
		? candidates.find((m) => m.user_id === issue.assignee_id)
		: null
	const waitingOnMe = issue.assignee_id === user?.user_id
	const canAssign = Boolean(issue.can_assign)
	const agent = candidates.find((m) => m.is_agent && m.user_id !== issue.assignee_id)

	function handTo(userId) {
		onAssign(userId, note.trim())
		setNote("")
	}

	return (
		<div
			className={cx(
				"flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border px-3.5 py-2.5",
				assignee
					? waitingOnMe
						? "border-state-open/35 bg-state-open-soft"
						: "border-ink-200/80 bg-white"
					: "border-dashed border-ink-200 bg-white"
			)}
		>
			<div className="flex min-w-0 items-center gap-2">
				<UserRound
					size={15}
					className={cx("shrink-0", assignee ? "text-state-open" : "text-ink-400")}
					aria-hidden
				/>
				{assignee ? (
					<span className="flex min-w-0 flex-wrap items-center gap-1.5">
						<span className="text-[0.8125rem] text-ink-600">Waiting on</span>
						<Avatar person={assignee} size="xs" />
						<span className="text-[0.8125rem] font-semibold text-ink-900">
							{waitingOnMe ? "you" : assignee.name}
						</span>
						<span
							className="text-2xs text-ink-500"
							title={issue.assigned_at}
						>
							since {relativeTime(issue.assigned_at, timezone)}
						</span>
					</span>
				) : (
					<span className="text-[0.8125rem] text-ink-500">
						Nobody has been asked to respond yet.
					</span>
				)}
			</div>

			<div className="ml-auto flex shrink-0 items-center gap-2">
				{onInvite && issue.can_invite && (
					<Button size="sm" variant="ghost" icon={UserRoundPlus} onClick={onInvite}>
						Invite
					</Button>
				)}

				{canAssign && agent && (
					<Button
						size="sm"
						variant="ghost"
						icon={Bot}
						onClick={() => handTo(agent.user_id)}
						busy={busy}
						disabled={busy}
						title={closed ? "Reopen and hand to " + agent.name : undefined}
					>
						{closed ? `Reopen for ${agent.name}` : `Hand to ${agent.name}`}
					</Button>
				)}

				{canAssign ? (
					<Popover
						className="w-72"
						trigger={({ toggle, open }) => (
							<Button
								size="sm"
								variant={assignee ? "secondary" : "primary"}
								icon={UserRound}
								onClick={toggle}
								aria-expanded={open}
								busy={busy}
								disabled={busy}
							>
								{closed
									? "Reopen and ask someone"
									: assignee
										? "Reassign"
										: "Ask someone to respond"}
							</Button>
						)}
					>
						{({ close }) => (
							<>
								<p className="px-2.5 pb-1 pt-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-400">
									Ask for a response
								</p>
								<div className="max-h-56 overflow-y-auto scrollbar-slim">
									{candidates
										.filter((member) => member.user_id !== issue.assignee_id)
										.map((member) => (
											<button
												key={member.user_id}
												type="button"
												onClick={() => {
													handTo(member.user_id)
													close()
												}}
												className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-ink-100"
											>
												<Avatar person={member} size="sm" />
												<span className="min-w-0 flex-1">
													<span className="flex items-center gap-1.5 truncate text-[0.8125rem] font-medium leading-tight text-ink-900">
														{member.user_id === user?.user_id
															? "Myself"
															: member.name}
														{member.is_agent && (
															<span className="inline-flex items-center gap-0.5 rounded bg-brand-50 px-1 py-px text-[0.625rem] font-semibold uppercase tracking-wide text-brand-700">
																<Bot size={10} aria-hidden />
																Agent
															</span>
														)}
													</span>
													<span className="block truncate text-2xs leading-tight text-ink-500">
														{[member.role, member.company]
															.filter(Boolean)
															.join(", ")}
													</span>
												</span>
											</button>
										))}
								</div>

								<div className="border-t border-ink-100 px-1.5 pb-1 pt-2">
									<Input
										value={note}
										onChange={(event) => setNote(event.target.value)}
										placeholder="Optional note to go with it"
										className="h-8 text-[0.8125rem]"
										maxLength={200}
									/>
								</div>

								{assignee && (
									<button
										type="button"
										onClick={() => {
											onAssign(null, "")
											close()
										}}
										className="mt-1 flex w-full items-center gap-2.5 rounded-lg border-t border-ink-100 px-2.5 py-2 text-left text-[0.8125rem] text-ink-600 transition-colors hover:bg-ink-100"
									>
										<UserMinus size={14} className="text-ink-400" aria-hidden />
										Clear the assignment
									</button>
								)}
							</>
						)}
					</Popover>
				) : (
					<span className="text-2xs text-ink-400">
						{closed
							? "Reply to hand this back to someone"
							: "Only whoever wrote the last entry can reassign"}
					</span>
				)}

				{closed && (
					<span className="inline-flex items-center gap-1.5 text-2xs text-state-closed-ink">
						<Check size={12} aria-hidden />
						Resolved
					</span>
				)}
			</div>
		</div>
	)
}
